import {
  sdk,
  isAsosAuthTimeoutError,
  isAsosAuthStorageError,
  ACQUIRE_TOKEN_MODES,
  ClientType
} from "@asosteam/asos-web-auth-client";
import {
  getStoreCode,
  getCountryCode,
  getKeyStoreDataversion,
  getDefaultLanguage
} from "../../template/state/modules/regionalStore";
import { AUTH_LAST_ACTION } from "../constants";
import { noticeError } from "@src/helpers/monitoring";

const {
  SIGNIN_EMAIL,
  SIGNIN_SOCIAL,
  SIGNUP_EMAIL,
  SIGNUP_SOCIAL,
  LINK_ACCOUNTS
} = AUTH_LAST_ACTION;

const defaultGetAccessTokenOptions = {
  retries: 0,
  timeoutMs: 20_000
};

export const initialise = ({ store, getSubscriptionDetails }) => {
  const state = store.getState();
  const context = {
    store: getStoreCode(state),
    keyStoreDataversion: getKeyStoreDataversion(state),
    lang: getDefaultLanguage(state),
    country: getCountryCode(state)
  };

  const { authClient } = sdk({
    clientId: "www",
    scopes: ["openid", "profile", "offline_access", "sensitive"],
    context,
    clientType: ClientType.WWW
  });

  const {
    isAuthenticated,
    isAnonymous,
    initialise: initialiseAuthClient,
    getCustomerAnonymousId,
    getIdTokenClaims,
    getIdTokenClaimsAsync,
    getAccessToken,
    redirectToLogin,
    redirectToSignUp,
    logout,
    getAuthHeaderPayload,
    isAuthHeaderCached,
    silentTokenRefresh
  } = authClient;

  const initialisePromise = initialiseAuthClient().catch(noticeError);

  const requireInitialise =
    method =>
    async (...params) => {
      await initialisePromise;

      return method(...params);
    };

  const getAccessTokenInterface = async ({
    retries = defaultGetAccessTokenOptions.retries,
    timeoutMs = defaultGetAccessTokenOptions.timeoutMs
  } = defaultGetAccessTokenOptions) =>
    await requireInitialise(getAccessToken)({
      acquireTokenMode: ACQUIRE_TOKEN_MODES.SILENT,
      retries,
      timeoutMs
    });

  const refreshToken = requireInitialise(silentTokenRefresh);

  let idTokenClaims;
  const getClaim = requireInitialise(
    async (field, anonymousValue = undefined) => {
      let returnValue;
      try {
        idTokenClaims = getIdTokenClaims();

        returnValue = idTokenClaims[field];
      } catch (_) {
        // fallback to anonymous below
      }

      return returnValue ? returnValue : anonymousValue;
    }
  );

  const profile = async () => {
    const lastAction = await getClaim("lastAction");

    return {
      customerId: await getClaim("customerId"),
      firstTimeBuyer: await getClaim("isFirstTimeBuyer"),
      firstName: await getClaim("firstName"),
      lastName: await getClaim("lastName"),
      customerAid: await requireInitialise(getCustomerAnonymousId)(),
      isAnonymous: await requireInitialise(isAnonymous)(),
      isRecognised: false,
      lastAction: {
        isSigninEmail: Boolean(lastAction === SIGNIN_EMAIL),
        isSigninSocial: Boolean(lastAction === SIGNIN_SOCIAL),
        isSignupEmail: Boolean(lastAction === SIGNUP_EMAIL),
        isSignupSocial: Boolean(lastAction === SIGNUP_SOCIAL),
        isLinkAccounts: Boolean(lastAction === LINK_ACCOUNTS)
      }
    };
  };

  const customerId = () => getClaim("customerId");

  const getAuthHeader = requireInitialise(
    async ({
      retries = defaultGetAccessTokenOptions.retries,
      timeoutMs = defaultGetAccessTokenOptions.timeoutMs
    } = defaultGetAccessTokenOptions) =>
      getAuthHeaderPayload({
        retries,
        timeoutMs
      })
  );

  const isAuthenticatedInterface = requireInitialise(isAuthenticated);

  const customer = {
    anonymousId: requireInitialise(getCustomerAnonymousId),
    customerGuid: () =>
      getClaim("customerGuid", requireInitialise(getCustomerAnonymousId)()),
    customerId,
    firstTimeBuyer: () => getClaim("isFirstTimeBuyer"),
    firstName: () => getClaim("firstName"),
    lastName: () => getClaim("lastName"),
    subscriptions: async () =>
      [await getClaim("customerSubscriptionCountry")].filter(Boolean),
    subscriptionDetails: () =>
      getSubscriptionDetails({
        getCustomerId: customerId,
        getAuthHeader,
        isAuthenticated: isAuthenticatedInterface
      }),
    premierPropositionId: async () =>
      [await getClaim("premierPropositionId")].filter(Boolean),
    profile,
    isAuthenticated: isAuthenticatedInterface,
    isRecognised: async () => false
  };

  return {
    customer,
    getAccessToken: getAccessTokenInterface,
    getAuthHeader,
    login: requireInitialise(redirectToLogin),
    signUp: requireInitialise(redirectToSignUp),
    logout: requireInitialise(logout),
    isAsosAuthTimeoutError,
    isAuthHeaderCached,
    isAsosAuthStorageError,
    getClaim,
    refreshToken,
    getIdTokenClaimsAsync: requireInitialise(getIdTokenClaimsAsync)
  };
};
